import React, { useEffect, useRef, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { UserContext } from '../UserContext';
import dataService from '../services/dataService';
import InputArea from '../components/Chat/InputArea';
import TopMenuBar from '../components/Chat/TopMenuBar';
import AddReviewModal from '../components/Chat/AddReviewModal';
import EditReviewsModal from '../components/Chat/EditReviewsModal';
import DishViewer from '../components/Chat/DishViewer';
import TypingIndicator from '../components/Chat/TypingIndicator';
import QuickPreferencesPanel from '../components/Chat/QuickPreferencesPanel';

import { List, Avatar, Card, CardContent, Box, Typography, Container } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { Snackbar, Alert } from '@mui/material';
import Markdown from 'react-markdown';

// Feature flag: Temporarily disable image generation until API key is configured
const ENABLE_DISH_IMAGES = true;

function ChatScreen() {
  const bottomListRef = useRef(null);

  const location = useLocation();
  const { userId } = useContext(UserContext);
  const restaurantId = location.state?.restaurantId;
  const restaurantName = location.state?.restaurantName;
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(-1);
  const [showAddReview, setShowAddReview] = useState(false);
  const [showEditReviews, setShowEditReviews] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [alert, setAlert] = useState(null);
  const [menuItemsFromChat, setMenuItemsFromChat] = useState([]);
  const [showQuickPrefs, setShowQuickPrefs] = useState(false);
  const [quickPrefs, setQuickPrefs] = useState({
    allergies: {
      gluten: false,
      dairy: false,
      eggs: false,
      fish: false,
      peanuts: false,
      shellfish: false,
      soy: false,
      tree_nuts: false
    },
    taste: {
      spicy: 5,
      salty: 5,
      sweet: 5,
      sour: 5,
      bitter: 5,
      savory: 5
    }
  });

  useEffect(() => {
    bottomListRef.current?.scrollIntoView({ behavior: "smooth" });
    const fetchItems = async () => {
      if (messages.length <= 4) return;
      try {
        let data = await dataService.menuItemsFromChat(chatId);
        setMenuItemsFromChat(data['items']);
      } catch (error) {
        console.log(error);
      }
    };
    fetchItems();
  }, [messages, chatId]);

  useEffect(() => {
    const fetchChatData = async () => {
      try {
        const chatData = await dataService.startChat(userId, restaurantId);
        setChatId(chatData.id);
        setMessages(JSON.parse(chatData.conversation_data));
      } catch (error) {
        console.log(error);
      }
    };

    fetchChatData();
  }, [userId, restaurantId]);

  const sendMessage = async (inputMsg) => {
    setMessages((msgs) => [...msgs, { role: 'user', content: inputMsg }]);
    setLoading(true);
    try {
      const chatData = await dataService.sendMessage(chatId, inputMsg);
      setLoading(false);
      setMessages((msgs) => [...msgs, chatData.response]);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const newChat = async () => {
    if (restaurantId === undefined || restaurantId === null) {
      return;
    }
    try {
      const chatData = await dataService.startChat(userId, restaurantId, true);
      setChatId(chatData.id);
      setMessages(JSON.parse(chatData.conversation_data));
      setMenuItemsFromChat([]);
    } catch (error) {
      console.log(error);
    }
  };

  const addRating = async ({ menuItem, rating, extraText }) => {
    try {
      await dataService.submitReview(userId, restaurantId, chatId, menuItem, rating, extraText);
      setShowAddReview(false);
      setAlert("Review saved!");
    } catch (error) {
      alert(error);
    }
  };

  const modifyReviews = async (reviews) => {
    await dataService.editUserRestaurantReviews(userId, restaurantId, reviews);
    setShowEditReviews(false);
    setAlert('Saved successfully!');
  };

  const loadAndShowReviews = async () => {
    let reviewData = await dataService.listUserRestaurantReviews(userId, restaurantId);
    await setReviews(reviewData);
    setShowEditReviews(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <TopMenuBar
        restaurantName={restaurantName}
        onNewChat={newChat}
        onAddReview={() => setShowAddReview(true)}
        onEditReviews={loadAndShowReviews}
        onQuickPrefs={() => setShowQuickPrefs(true)}
      />
      <AddReviewModal
        open={showAddReview}
        onClose={() => setShowAddReview(false)}
        onSubmit={addRating}
        suggested={menuItemsFromChat}
      />
      <EditReviewsModal
        open={showEditReviews}
        onClose={() => setShowEditReviews(false)}
        onSubmit={modifyReviews}
        reviews={reviews}
      />
      <QuickPreferencesPanel
        open={showQuickPrefs}
        onClose={() => setShowQuickPrefs(false)}
        currentPrefs={quickPrefs}
        onChange={(newPrefs) => {
          setQuickPrefs(newPrefs);
          setAlert('Preferences updated for this session');
        }}
      />
      <Snackbar
        open={alert !== null}
        autoHideDuration={6000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>{alert}</Alert>
      </Snackbar>

      <Container maxWidth="md" sx={{ flex: 1, overflow: 'auto', py: 3 }}>
        <List sx={{ pb: 2 }}>
          {messages && messages.map((message, index) => {
            if (message.role === 'system') return null;
            const isUser = message.role === 'user';
            return (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                  border: 'none',
                  bgcolor: isUser ? 'primary.main' : 'grey.50',
                  maxWidth: '85%',
                  ml: isUser ? 'auto' : 0,
                  mr: isUser ? 0 : 'auto',
                  boxShadow: 1,
                }}
              >
                <CardContent sx={{
                  display: 'flex',
                  flexDirection: isUser ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  py: 2,
                  px: 2,
                  '&:last-child': { pb: 2 },
                }}>
                  <Avatar
                    sx={{
                      bgcolor: isUser ? 'primary.dark' : 'secondary.main',
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                    }}
                  >
                    {isUser ? <PersonIcon fontSize="small" /> : <RestaurantIcon fontSize="small" />}
                  </Avatar>
                  <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    '& p': { margin: 0 },
                    '& p + p': { mt: 1 },
                    '& ul, & ol': { my: 1, pl: 2.5 },
                    '& li': { mb: 0.5 },
                  }}>
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        color: isUser ? 'white' : 'text.primary',
                        lineHeight: 1.6,
                        '& strong': { fontWeight: 600 },
                      }}
                    >
                      <Markdown>{message.content}</Markdown>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          <div ref={bottomListRef} />
        </List>

        {ENABLE_DISH_IMAGES && menuItemsFromChat.length > 0 && (
          <DishViewer restaurantId={restaurantId} dishes={menuItemsFromChat} />
        )}

        {loading && <TypingIndicator />}
      </Container>

      <InputArea onSend={sendMessage} />
    </Box>
  );
}

export default ChatScreen;
